import type { PaletteMode } from '@mui/material/styles';
import type {
  ThemeStudioDensity,
  ThemeStudioFontFamily,
  ThemeStudioFontScale,
  ThemeStudioPreferences,
  ThemeStudioSidebarStyle,
} from '@/shell/AppShellContext';

export interface ThemeDefinition {
  id: string;
  name: string;
  mode: PaletteMode;
  primary: string;
  secondary: string;
  surface: string;
  background: string;
  border: string;
  typography: {
    fontFamily: ThemeStudioFontFamily;
    fontScale: ThemeStudioFontScale;
  };
  radius: number;
  spacing: ThemeStudioDensity;
  elevation: number;
  sidebar: {
    background: string;
    text: string;
  };
  header: {
    background: string;
    text: string;
  };
  cards: {
    background: string;
    border: string;
  };
}

export const portalThemeDefinitions: ThemeDefinition[] = [
  {
    id: 'creditsafe-classic',
    name: 'Creditsafe Classic',
    mode: 'light',
    primary: '#ee2e24',
    secondary: '#455a64',
    surface: '#ffffff',
    background: '#f5f7fa',
    border: '#e4e9f0',
    typography: { fontFamily: 'inter', fontScale: 'medium' },
    radius: 8,
    spacing: 'comfortable',
    elevation: 1,
    sidebar: { background: '#ffffff', text: '#1a1a2e' },
    header: { background: '#ffffff', text: '#1a1a2e' },
    cards: { background: '#ffffff', border: '#e4e9f0' },
  },
  {
    id: 'enterprise-blue',
    name: 'Enterprise Blue',
    mode: 'light',
    primary: '#1866f2',
    secondary: '#1e3a8a',
    surface: '#ffffff',
    background: '#f4f7ff',
    border: '#dce6ff',
    typography: { fontFamily: 'segoe', fontScale: 'medium' },
    radius: 10,
    spacing: 'comfortable',
    elevation: 1,
    sidebar: { background: '#ffffff', text: '#102a56' },
    header: { background: '#ffffff', text: '#102a56' },
    cards: { background: '#ffffff', border: '#dce6ff' },
  },
  {
    id: 'modern-dark',
    name: 'Modern Dark',
    mode: 'dark',
    primary: '#7aa2ff',
    secondary: '#3f4a66',
    surface: '#172033',
    background: '#0f1724',
    border: '#2a3750',
    typography: { fontFamily: 'inter', fontScale: 'medium' },
    radius: 10,
    spacing: 'comfortable',
    elevation: 1,
    sidebar: { background: '#121b2d', text: '#d8e4ff' },
    header: { background: '#121b2d', text: '#d8e4ff' },
    cards: { background: '#172033', border: '#2a3750' },
  },
  {
    id: 'executive',
    name: 'Executive',
    mode: 'light',
    primary: '#1b2a4a',
    secondary: '#5c7399',
    surface: '#ffffff',
    background: '#f8fafc',
    border: '#dbe3ef',
    typography: { fontFamily: 'roboto', fontScale: 'medium' },
    radius: 6,
    spacing: 'comfortable',
    elevation: 1,
    sidebar: { background: '#ffffff', text: '#1b2a4a' },
    header: { background: '#ffffff', text: '#1b2a4a' },
    cards: { background: '#ffffff', border: '#dbe3ef' },
  },
  {
    id: 'minimal',
    name: 'Minimal',
    mode: 'light',
    primary: '#334155',
    secondary: '#94a3b8',
    surface: '#ffffff',
    background: '#f8fafc',
    border: '#e2e8f0',
    typography: { fontFamily: 'system', fontScale: 'small' },
    radius: 12,
    spacing: 'spacious',
    elevation: 0,
    sidebar: { background: '#ffffff', text: '#334155' },
    header: { background: '#ffffff', text: '#334155' },
    cards: { background: '#ffffff', border: '#e2e8f0' },
  },
  {
    id: 'corporate',
    name: 'Corporate',
    mode: 'light',
    primary: '#0f766e',
    secondary: '#155e75',
    surface: '#ffffff',
    background: '#f0fdfa',
    border: '#cce7e3',
    typography: { fontFamily: 'segoe', fontScale: 'medium' },
    radius: 8,
    spacing: 'comfortable',
    elevation: 1,
    sidebar: { background: '#ffffff', text: '#0f4d49' },
    header: { background: '#ffffff', text: '#0f4d49' },
    cards: { background: '#ffffff', border: '#cce7e3' },
  },
  {
    id: 'glass',
    name: 'Glass',
    mode: 'light',
    primary: '#6366f1',
    secondary: '#8b5cf6',
    surface: '#ffffffcc',
    background: '#eef2ff',
    border: '#c7d2fe',
    typography: { fontFamily: 'inter', fontScale: 'medium' },
    radius: 14,
    spacing: 'spacious',
    elevation: 2,
    sidebar: { background: '#ffffffd9', text: '#312e81' },
    header: { background: '#ffffffd9', text: '#312e81' },
    cards: { background: '#ffffffcc', border: '#c7d2fe' },
  },
];

export const enterpriseAccentPalette = [
  '#ee2e24',
  '#1866f2',
  '#0f766e',
  '#7c3aed',
  '#0284c7',
  '#0891b2',
  '#4f46e5',
  '#ca8a04',
  '#059669',
  '#b91c1c',
];

export const layoutDensityOptions: Array<{
  id: ThemeStudioDensity;
  label: string;
}> = [
  { id: 'compact', label: 'Compact' },
  { id: 'comfortable', label: 'Comfortable' },
  { id: 'spacious', label: 'Spacious' },
];

export const sidebarStyleOptions: Array<{
  id: ThemeStudioSidebarStyle;
  label: string;
  description: string;
}> = [
  { id: 'default', label: 'Default', description: 'Balanced navigation width' },
  { id: 'compact', label: 'Compact', description: 'Narrow rail with labels' },
  { id: 'floating', label: 'Floating', description: 'Detached panel style' },
  { id: 'collapsed', label: 'Collapsed', description: 'Icon-first navigation' },
];

export const fontFamilyOptions: Array<{
  id: ThemeStudioFontFamily;
  label: string;
  family: string;
}> = [
  { id: 'inter', label: 'Inter', family: 'Inter, sans-serif' },
  { id: 'segoe', label: 'Segoe UI', family: 'Segoe UI, sans-serif' },
  { id: 'roboto', label: 'Roboto', family: 'Roboto, sans-serif' },
  { id: 'system', label: 'System', family: 'system-ui, sans-serif' },
];

export const fontScaleOptions: Array<{
  id: ThemeStudioFontScale;
  label: string;
  scale: number;
}> = [
  { id: 'small', label: 'Small', scale: 0.93 },
  { id: 'medium', label: 'Medium', scale: 1 },
  { id: 'large', label: 'Large', scale: 1.08 },
];

export const defaultThemeStudioPreferences: ThemeStudioPreferences = {
  mode: 'system',
  themeId: 'creditsafe-classic',
  accentColor: '#ee2e24',
  layoutDensity: 'comfortable',
  sidebarStyle: 'default',
  roundedCorners: true,
  animations: true,
  reducedMotion: false,
  denseTables: false,
  stickyHeaders: true,
  fontFamily: 'inter',
  fontScale: 'medium',
  recentColors: ['#ee2e24', '#1866f2', '#0f766e'],
};

export function getThemeDefinition(themeId: string): ThemeDefinition {
  return (
    portalThemeDefinitions.find((theme) => theme.id === themeId) ??
    portalThemeDefinitions[0]
  );
}
